import React, { memo, useState } from 'react';

import Box from '../../components/Box';
import { BlockColors } from '@/types/blocks';
import clsx from 'clsx';

interface Props {}

const Squiggles: React.FC<Props> = memo(() => {
  const [width, setWidth] = useState(320);
  const [height, setHeight] = useState(240);

  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWidth(Number(e.target.value));
  };

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHeight(Number(e.target.value));
  };

  return (
    <Box>
      <h1 className="text-2xl font-bold mb-4">Squiggles Showcase</h1>

      <div className="mb-6 space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="width-slider" className="text-sm font-medium">
              Width: {width}px
            </label>
            <span className="text-xs text-gray-500">120-1024</span>
          </div>
          <input
            id="width-slider"
            type="range"
            min="120"
            max="1024"
            value={width}
            onChange={handleWidthChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="height-slider" className="text-sm font-medium">
              Height: {height}px
            </label>
            <span className="text-xs text-gray-500">120-1024</span>
          </div>
          <input
            id="height-slider"
            type="range"
            min="120"
            max="1024"
            value={height}
            onChange={handleHeightChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>
      </div>

      <SquiggleDocument width={width} height={height} />
    </Box>
  );
});
Squiggles.displayName = 'Squiggles';

export default Squiggles;

// Point represents an x,y coordinate
interface Point {
  x: number;
  y: number;
}

// Squiggle represents a complete squiggly line with a series of points
// and styling properties
interface Squiggle {
  points: Point[];
  color: string;
  width: number;
  opacity: number;
}

// Enum for the different corners where a squiggle can start/end
enum Corner {
  TopLeft,
  TopRight,
  BottomLeft,
  BottomRight,
}

const useSquiggles = (
  { width: boxWidth, height: boxHeight }: { width: number; height: number },
  count = 4,
) => {
  // Generate elegant, calm squiggles that start and end off-screen
  const generateSquiggles = (): Squiggle[] => {
    const squiggles: Squiggle[] = [];

    // Generate one squiggle for each corner
    const corners = [
      Corner.TopLeft,
      Corner.TopRight,
      Corner.BottomLeft,
      Corner.BottomRight,
    ];

    for (let i = 0; i < Math.min(count, corners.length); i++) {
      const corner = corners[i];
      // Use the same corner for both start and end points
      const startCorner = corner;
      const endCorner = corner;

      const color = BlockColors[
        Math.floor(Math.random() * BlockColors.length)
      ] as (typeof BlockColors)[number];

      // Generate elegant width and opacity
      const width =
        (3.6 + Math.random() * 1.2) * (Math.max(boxWidth, boxHeight) / 180) +
        10; // Slightly thicker (1.2-2.0)
      const opacity = 1; //0.7 + Math.random() * 0.3; // More visible (0.7-1.0)

      // Generate the squiggle with a flow-based approach
      const points = generateFlowBasedSquiggle(startCorner, endCorner, {
        width: boxWidth,
        height: boxHeight,
      });

      squiggles.push({
        points,
        color,
        width,
        opacity,
      });
    }

    return squiggles;
  };

  // Elegant, sweeping squiggle generator
  // Creates smooth, flowing curves with graceful loops
  const generateFlowBasedSquiggle = (
    corner: Corner,
    endCorner: Corner,
    { width, height }: { width: number; height: number },
  ): Point[] => {
    // Define the document center
    const centerX = width / 2;
    const centerY = height / 2;

    // Define corner coordinates and angles
    let cornerX: number, cornerY: number, cornerAngle: number;
    switch (corner) {
      case Corner.TopLeft:
        cornerX = 0;
        cornerY = 0;
        cornerAngle = Math.PI * 0.25; // 45 degrees
        break;
      case Corner.TopRight:
        cornerX = width;
        cornerY = 0;
        cornerAngle = Math.PI * 0.75; // 135 degrees
        break;
      case Corner.BottomLeft:
        cornerX = 0;
        cornerY = height;
        cornerAngle = Math.PI * 1.75; // 315 degrees
        break;
      case Corner.BottomRight:
        cornerX = width;
        cornerY = height;
        cornerAngle = Math.PI * 1.25; // 225 degrees
        break;
    }

    // Define off-screen starting and ending points
    const startPoint = getOffscreenPoint(corner, { width, height });
    const endPoint = getOffscreenPoint(corner, { width, height }); // Same corner for end

    // Array to hold our points
    const points: Point[] = [];

    // Add the starting point
    points.push(startPoint);

    // Define an entry point just inside the visible area
    const entryPoint = {
      x: cornerX + Math.cos(cornerAngle) * 5,
      y: cornerY + Math.sin(cornerAngle) * 5,
    };

    // Create a smooth transition from off-screen to on-screen
    // Use more intermediate points for a smoother entry
    const entryPoints = 15;
    for (let i = 1; i <= entryPoints; i++) {
      const t = i / (entryPoints + 1);

      // Use a cubic easing function for smoother entry
      const easedT = t * t * t;

      const x = startPoint.x * (1 - easedT) + entryPoint.x * easedT;
      const y = startPoint.y * (1 - easedT) + entryPoint.y * easedT;
      points.push({ x, y });
    }

    // Add the entry point
    points.push(entryPoint);

    // Define control parameters for the elegant loops

    // Direction (clockwise or counter-clockwise)
    const clockwise = Math.random() > 0.5 ? 1 : -1;

    // Vary the center pull for each corner to create diversity
    // Some squiggles will reach further toward the center than others
    let centerPull: number;

    // Assign different center pull values to different corners
    // This creates more visual interest across the canvas
    switch (corner) {
      case Corner.TopLeft:
        // centerPull = 0.7 + Math.random() * 0.2; // 0.7-0.9 (reaches far toward center)
        centerPull = 0.6 + Math.random() * 0.2; // 0.6-0.8 (good reach)
        break;
      case Corner.TopRight:
        // centerPull = 0.5 + Math.random() * 0.2; // 0.5-0.7 (moderate reach)
        centerPull = 0.6 + Math.random() * 0.2; // 0.6-0.8 (good reach)
        break;
      case Corner.BottomLeft:
        centerPull = 0.6 + Math.random() * 0.2; // 0.6-0.8 (good reach)
        break;
      case Corner.BottomRight:
        // centerPull = 0.8 + Math.random() * 0.15; // 0.8-0.95 (reaches very far toward center)
        centerPull = 0.6 + Math.random() * 0.2; // 0.6-0.8 (good reach)
        break;
    }

    // Calculate the main control point that guides the overall path
    // This point pulls the squiggle toward the center
    const controlX = cornerX * (1 - centerPull) + centerX * centerPull;
    const controlY = cornerY * (1 - centerPull) + centerY * centerPull;

    // Number of loops to create - more loops for squiggles that reach further
    // const numLoops = 4 + Math.floor(Math.random() * 3); // 4-6 loops
    const numLoops = 2 + Math.floor(Math.random() * 3); // 4-6 loops

    // Generate the main sweeping path with elegant loops
    const numPoints = 30; // High number of points for smooth curves

    for (let i = 0; i <= numPoints; i++) {
      // Progress along the path (0 to 1)
      const t = i / numPoints;

      // Use a smooth easing function for the path
      // This creates a natural flow from corner to center and back
      const ease = Math.sin(t * Math.PI);

      // Base path follows a quadratic curve from corner toward center and back
      // This creates the overall sweeping motion
      const baseT = t < 0.5 ? t * 2 : (1 - t) * 2; // 0→1→0

      // Calculate base position along the quadratic curve
      const baseX =
        cornerX * (1 - baseT) * (1 - baseT) +
        2 * controlX * baseT * (1 - baseT) +
        cornerX * baseT * baseT;

      const baseY =
        cornerY * (1 - baseT) * (1 - baseT) +
        2 * controlY * baseT * (1 - baseT) +
        cornerY * baseT * baseT;

      // Add elegant loops to the base path
      // The loops are larger in the middle and smaller at the ends
      const loopSize = (20 + Math.floor(i / 2)) * ease * (width / 50);

      // Create slightly oval loops based on the corner
      let xScale, yScale;

      // Adjust loop shape based on corner position
      if (corner === Corner.TopLeft || corner === Corner.BottomRight) {
        // For diagonal corners, make loops that follow the diagonal
        xScale = 0.9;
        yScale = 1.1;
      } else {
        // For other corners, make loops perpendicular to the diagonal
        xScale = 1.1;
        yScale = 0.9;
      }

      // Calculate continuous loop progress
      const loopFrequency = numLoops * Math.PI * 2;
      const continuousLoopProgress = t * numLoops; // Continuous progress through all loops
      const loopProgress = continuousLoopProgress % 1; // 0-1 within current loop

      // Loop angle increases smoothly to create the looping effect
      const loopAngle = loopProgress * Math.PI * 2 * clockwise;

      // Create a smooth, continuous drift effect
      // This creates the effect of a hand moving across the page without jagged transitions

      // Calculate drift direction - perpendicular to the line from corner to center
      const driftDirection =
        Math.atan2(centerY - cornerY, centerX - cornerX) + Math.PI / 2;

      // Use a combination of sine waves for the drift to create a more natural, flowing movement
      // This creates a complex motion that mimics the natural variation in hand-drawn curves

      // Primary drift - smooth side-to-side motion
      const driftAmplitude = boxWidth / 4; // Maximum drift distance
      const driftPhase = t * Math.PI * 2 * 0.1; // Slower than the loops

      // Secondary drift - adds a gentle wandering quality
      const secondaryDriftAmplitude = 5;
      const secondaryDriftPhase = t * Math.PI * 3.7; // Different frequency

      // Combine the two drift patterns
      const driftX =
        Math.cos(driftDirection) * Math.sin(driftPhase) * driftAmplitude +
        Math.cos(driftDirection + Math.PI / 4) *
          Math.sin(secondaryDriftPhase) *
          secondaryDriftAmplitude;

      const driftY =
        Math.sin(driftDirection) * Math.sin(driftPhase) * driftAmplitude +
        Math.sin(driftDirection + Math.PI / 4) *
          Math.sin(secondaryDriftPhase) *
          secondaryDriftAmplitude;

      // Apply initial drift to the base position
      let driftedBaseX = baseX + driftX;
      let driftedBaseY = baseY + driftY;

      // Add a center-pulling component to the drift
      // This gradually pulls some loops toward the center
      const centerPullFactor = Math.sin(t * Math.PI) * 0.3; // Strongest in the middle
      const centerPullX = (centerX - driftedBaseX) * centerPullFactor;
      const centerPullY = (centerY - driftedBaseY) * centerPullFactor;

      // Apply center pull to update the position
      driftedBaseX += centerPullX;
      driftedBaseY += centerPullY;

      // Calculate the final position with the loop added
      const x = driftedBaseX + Math.cos(loopAngle) * loopSize * xScale;
      const y = driftedBaseY + Math.sin(loopAngle) * loopSize * yScale;

      // Add the point to our path
      points.push({ x, y });
    }

    // Define an exit point just inside the visible area
    const exitPoint = {
      x: cornerX + Math.cos(cornerAngle + Math.PI) * 5,
      y: cornerY + Math.sin(cornerAngle + Math.PI) * 5,
    };

    // Add the exit point
    points.push(exitPoint);

    // Create a smooth transition from on-screen to off-screen
    // Use more intermediate points for a smoother exit
    const exitPoints = 15;
    for (let i = 1; i <= exitPoints; i++) {
      const t = i / (exitPoints + 1);

      // Use a cubic easing function for smoother exit
      const easedT = t * t * t;

      const x = exitPoint.x * (1 - easedT) + endPoint.x * easedT;
      const y = exitPoint.y * (1 - easedT) + endPoint.y * easedT;
      points.push({ x, y });
    }

    // Add the ending point
    points.push(endPoint);

    return points;
  };

  // Get an off-screen point based on the corner
  // These points are further off-screen to ensure they're not visible
  const getOffscreenPoint = (
    corner: Corner,
    { width, height }: { width: number; height: number },
  ): Point => {
    const PaddingFactor = 0.8;
    switch (corner) {
      case Corner.TopLeft:
        return { x: -(width * PaddingFactor), y: -(height * PaddingFactor) };
      case Corner.TopRight:
        return {
          x: width + width * PaddingFactor,
          y: -(height * PaddingFactor),
        };
      case Corner.BottomLeft:
        return {
          x: -(width * PaddingFactor),
          y: height + height * PaddingFactor,
        };
      case Corner.BottomRight:
        return {
          x: width + width * PaddingFactor,
          y: height + height * PaddingFactor,
        };
    }
  };

  return {
    squiggles: generateSquiggles(),
  };
};

interface SquiggleDocumentProps {
  width: number;
  height: number;
  backgroundColor?: string;
}

const SquiggleDocument: React.FC<SquiggleDocumentProps> = ({
  width,
  height,
  backgroundColor = 'bg-gray-200',
}) => {
  // Generate squiggles with the correct aspect ratio
  const { squiggles } = useSquiggles({ width, height });

  // Convert dimensions to style objects for flexibility
  const containerStyle = {
    width: `${width}px`,
    height: `${height}px`,
  };

  return (
    <div
      className={`${backgroundColor} flex items-center justify-center`}
      style={containerStyle}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {squiggles.map((squiggle, index) => (
          <SvgSquiggle key={index} squiggle={squiggle} />
        ))}
      </svg>
    </div>
  );
};

const SvgSquiggle: React.FC<{ squiggle: Squiggle }> = ({ squiggle }) => {
  // Use a cardinal spline to create a smooth curve through all points
  // This is a more natural approach for hand-drawn looking curves
  const pathData = generateCardinalSpline(squiggle.points, 0.5);

  return (
    <path
      d={pathData}
      fill="none"
      className={clsx('stroke-current', {
        'text-black': squiggle.color === 'black',
        'text-white': squiggle.color === 'white',
        'text-accent-yelo': squiggle.color === 'yellow',
        'text-accent-oranje': squiggle.color === 'orange',
        'text-accent-grun': squiggle.color === 'green',
        'text-accent-blau': squiggle.color === 'blue',
      })}
      strokeWidth={squiggle.width}
      strokeOpacity={squiggle.opacity}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
};

// Generate a cardinal spline through the given points
// tension: 0 = straight lines, 1 = maximum curve
const generateCardinalSpline = (
  points: Point[],
  tension: number = 0.5,
): string => {
  if (points.length < 2) return '';

  let path = `M ${points[0].x},${points[0].y}`;

  // For the first segment, we'll use a quadratic curve
  if (points.length > 1) {
    const p0 = points[0];
    const p1 = points[1];

    // If there's only two points, draw a line
    if (points.length === 2) {
      path += ` L ${p1.x},${p1.y}`;
      return path;
    }

    // Otherwise, start with a quadratic curve to the second point
    const p2 = points[2];

    // Calculate control point for quadratic curve
    const controlX = p1.x - ((p2.x - p0.x) * tension) / 2;
    const controlY = p1.y - ((p2.y - p0.y) * tension) / 2;

    path += ` Q ${controlX},${controlY} ${p1.x},${p1.y}`;
  }

  // For middle segments, use cubic bezier curves
  for (let i = 1; i < points.length - 2; i++) {
    const p0 = points[i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2];

    // Calculate control points for cubic bezier
    // This is the cardinal spline formula
    const control1X = p1.x + ((p2.x - p0.x) * tension) / 3;
    const control1Y = p1.y + ((p2.y - p0.y) * tension) / 3;

    const control2X = p2.x - ((p3.x - p1.x) * tension) / 3;
    const control2Y = p2.y - ((p3.y - p1.y) * tension) / 3;

    path += ` C ${control1X},${control1Y} ${control2X},${control2Y} ${p2.x},${p2.y}`;
  }

  // For the last segment, use a quadratic curve again
  if (points.length > 2) {
    const n = points.length;
    const p0 = points[n - 3];
    const p1 = points[n - 2];
    const p2 = points[n - 1];

    // Calculate control point for quadratic curve
    const controlX = p1.x + ((p2.x - p0.x) * tension) / 2;
    const controlY = p1.y + ((p2.y - p0.y) * tension) / 2;

    path += ` Q ${controlX},${controlY} ${p2.x},${p2.y}`;
  }

  return path;
};
